/**
 * <Select> — hybrid decomposed + composed Select primitive (D-05).
 *
 * Wraps `@radix-ui/react-select` with semantic tokens. Two surfaces:
 *
 *   1. **Decomposed** — styled re-exports of Radix parts. Apps reach for this
 *      when they need non-standard trigger content, grouped options with
 *      labels, or a custom value display.
 *
 *        <Select.Root value={v} onValueChange={setV}>
 *          <Select.Trigger><Select.Value placeholder="Pick one" /></Select.Trigger>
 *          <Select.Content>
 *            <Select.Item value="a">Alpha</Select.Item>
 *          </Select.Content>
 *        </Select.Root>
 *
 *   2. **Composed** — `<Select options={[{value,label}]} />` single-prop API for
 *      the 80% case.
 *
 * UI-SPEC defaults:
 *   - Trigger shares Input visual language: h-10, bg-surface-elevated,
 *     border-strong, rounded-md, focus-ring.
 *   - Content uses surface-elevated + shadow-lg + rounded-md.
 *   - Selected item uses `bg-primary-soft text-primary-ink` (per UI-SPEC rule 9 kin —
 *     primary-soft for selected, primary-fill reserved for checked state on
 *     Checkbox/Radio/Switch).
 *   - Highlighted (keyboard-focused) item uses `bg-surface-sunken`.
 *   - Logical properties only (ps-*, pe-*, ms-*, me-*).
 */
import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../utils";
import { Icon } from "../Icon";

// ---------------------------------------------------------------------------
// Decomposed — styled Radix parts
// ---------------------------------------------------------------------------

export const SelectRoot = RadixSelect.Root;
export const SelectValue = RadixSelect.Value;
export const SelectGroup = RadixSelect.Group;
export const SelectPortal = RadixSelect.Portal;
export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof RadixSelect.Separator>,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Separator>
>(function SelectSeparator({ className, ...rest }, ref) {
  return (
    <RadixSelect.Separator
      ref={ref}
      className={cn("my-xs h-px bg-border-subtle", className)}
      {...rest}
    />
  );
});

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof RadixSelect.Label>,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Label>
>(function SelectLabel({ className, ...rest }, ref) {
  return (
    <RadixSelect.Label ref={ref} className={cn("px-sm py-xs text-label", className)} {...rest} />
  );
});

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof RadixSelect.Trigger>,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Trigger>
>(function SelectTrigger({ className, children, ...rest }, ref) {
  return (
    <RadixSelect.Trigger
      ref={ref}
      className={cn(
        // Shape + layout (logical properties, Input parity)
        "inline-flex items-center justify-between gap-sm",
        "w-full h-10 rounded-md border appearance-none",
        "ps-md pe-sm",
        // Typography
        "text-body",
        // Colors — semantic tokens
        "bg-surface-elevated text-default border-subtle",
        "data-[placeholder]:text-muted",
        // Focus + states
        "focus-visible:focus-ring focus-visible:border-primary",
        "disabled:opacity-50 disabled:pointer-events-none",
        "data-[invalid=true]:border-danger data-[invalid=true]:focus-visible:border-danger",
        "transition-colors duration-fast ease-out",
        className,
      )}
      {...rest}
    >
      {children}
      <RadixSelect.Icon asChild>
        <Icon as={ChevronDown} name="chevron-down" size={16} />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
  );
});

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof RadixSelect.Content>,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Content>
>(function SelectContent(
  { className, children, position = "popper", sideOffset = 4, ...rest },
  ref,
) {
  return (
    <RadixSelect.Portal>
      <RadixSelect.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(
          // Surface
          "z-50 overflow-hidden rounded-md border border-subtle bg-surface-elevated shadow-lg",
          // Sizing — match trigger width when popper
          "min-w-[var(--radix-select-trigger-width)]",
          "max-h-[var(--radix-select-content-available-height)]",
          // Motion — pop: fade + scale 0.98, 140ms; origin from Radix data-side.
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-98 data-[state=open]:motion-popover",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-98 data-[state=closed]:motion-popover",
          "motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
          className,
        )}
        {...rest}
      >
        <RadixSelect.Viewport className="p-xs">{children}</RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  );
});

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof RadixSelect.Item>,
  React.ComponentPropsWithoutRef<typeof RadixSelect.Item>
>(function SelectItem({ className, children, ...rest }, ref) {
  return (
    <RadixSelect.Item
      ref={ref}
      className={cn(
        // Shape + layout
        "relative flex w-full cursor-default select-none items-center gap-sm",
        "rounded-sm py-sm ps-md pe-lg",
        // Typography
        "text-body outline-none",
        // States
        "data-[highlighted]:bg-surface-sunken",
        // Selected state — primary-soft tint + primary text (UI-SPEC §Color rule kin to selection)
        "data-[state=checked]:bg-primary-soft data-[state=checked]:text-primary-ink",
        "data-[disabled]:opacity-50 data-[disabled]:pointer-events-none",
        className,
      )}
      {...rest}
    >
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      <RadixSelect.ItemIndicator className="absolute end-sm inline-flex items-center">
        <Icon as={Check} name="check" size={14} />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
});

// ---------------------------------------------------------------------------
// Composed — single-prop convenience wrapper
// ---------------------------------------------------------------------------

/**
 * Internal sentinel used to represent the empty-string ("no value") option so
 * Radix Select's hard ban on `value=""` doesn't crash the consumer. Radix
 * reserves `""` for "clear the selection and show the placeholder"; we route
 * `""` ↔ this sentinel at the ComposedSelect boundary.
 *
 * Consumers can keep using `""` for "None"/"Any" options — the translation is
 * invisible outside this module. The sentinel is prefixed with a unicode BOM
 * + namespace so it cannot collide with any real value from the domain.
 * `""` with no empty option is passed through, so Radix shows the placeholder.
 */
const EMPTY_VALUE_SENTINEL = "​__linkhr_select_empty__";

function toRadix(value: string | undefined, hasEmptyOption: boolean): string | undefined {
  if (value === "" && hasEmptyOption) return EMPTY_VALUE_SENTINEL;
  return value;
}
function fromRadix(value: string): string {
  return value === EMPTY_VALUE_SENTINEL ? "" : value;
}

export interface SelectOption {
  /** Raw option value (string). `""` is allowed and represents a "none"/"any"
   * entry; the Select primitive internally maps it to a Radix-safe sentinel
   * so Radix doesn't crash on `Select.Item value=""`. */
  value: string;
  /** Human-readable label rendered inside the item + displayed when selected. */
  label: React.ReactNode;
  /** Disable this single option (other items remain selectable). */
  disabled?: boolean;
}

export interface SelectProps {
  /** Controlled value. Omit for uncontrolled + use `defaultValue`. */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;
  /** Controlled change handler. */
  onValueChange?: (value: string) => void;
  /** Disable the entire trigger. */
  disabled?: boolean;
  /** Mark invalid — drives danger border + sets `data-invalid` on trigger. */
  invalid?: boolean;
  /** Placeholder rendered inside the trigger when nothing is selected. */
  placeholder?: string;
  /** Option list — strings must be unique. */
  options: ReadonlyArray<SelectOption>;
  /** Name for form serialization (Radix forwards to a hidden input). */
  name?: string;
  /** Required attribute on the hidden form input. */
  required?: boolean;
  /** Consumer-supplied className forwarded to the trigger. */
  className?: string;
  /**
   * Trigger id. `<Field>` injects this (plus `aria-describedby` / `aria-invalid`)
   * into its child via `cloneElement`; without them declared here they were
   * destructured away and dropped, leaving the visible `<label for>` pointing at
   * an element that does not exist and the hint unlinked. Any control placed
   * inside a Field has to accept all three.
   */
  id?: string;
  /** Accessible label — required when no visible label is adjacent. */
  "aria-label"?: string;
  /** References id of a visible label element when present. */
  "aria-labelledby"?: string;
  /** Ids of hint / error text describing the trigger (injected by `<Field>`). */
  "aria-describedby"?: string;
  /**
   * Invalid state from an external validator. Typed as React's own union
   * because `<Field>` injects the ARIA *string* `"true"` / `"false"`, not a
   * boolean — a `=== true` check silently dropped it and a Select inside an
   * error-bearing Field lost both its danger border and its ARIA state.
   */
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
}

const ComposedSelect = React.forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    value,
    defaultValue,
    onValueChange,
    disabled,
    invalid,
    placeholder,
    options,
    name,
    required,
    className,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
  },
  ref,
) {
  const hasEmptyOption = options.some((o) => o.value === "");
  const radixValue = toRadix(value, hasEmptyOption);
  const radixDefault = toRadix(defaultValue, hasEmptyOption);
  const onValueChangeAdapted = onValueChange
    ? (v: string) => onValueChange(fromRadix(v))
    : undefined;
  const rootProps = {
    ...(radixValue !== undefined ? { value: radixValue } : {}),
    ...(radixDefault !== undefined ? { defaultValue: radixDefault } : {}),
    ...(onValueChangeAdapted !== undefined ? { onValueChange: onValueChangeAdapted } : {}),
    ...(disabled !== undefined ? { disabled } : {}),
    ...(name !== undefined ? { name } : {}),
    ...(required !== undefined ? { required } : {}),
  };
  // `invalid` (the DS prop) and `aria-invalid` (what Field injects when it has
  // error text) mean the same thing to a reader, so either one drives both the
  // danger border and the ARIA state. Field passes the ARIA string form, so
  // `"true"` counts and `"false"` does not.
  const isInvalid = invalid === true || ariaInvalid === true || ariaInvalid === "true";
  const triggerProps = {
    ...(className !== undefined ? { className } : {}),
    ...(id !== undefined ? { id } : {}),
    ...(ariaLabel !== undefined ? { "aria-label": ariaLabel } : {}),
    ...(ariaLabelledBy !== undefined ? { "aria-labelledby": ariaLabelledBy } : {}),
    ...(ariaDescribedBy !== undefined ? { "aria-describedby": ariaDescribedBy } : {}),
    ...(isInvalid ? { "data-invalid": "true" as const, "aria-invalid": true as const } : {}),
  };
  const valueProps = placeholder !== undefined ? { placeholder } : {};
  return (
    <RadixSelect.Root {...rootProps}>
      <SelectTrigger ref={ref} {...triggerProps}>
        <RadixSelect.Value {...valueProps} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => {
          const itemValue = toRadix(opt.value, hasEmptyOption) ?? opt.value;
          return (
            <SelectItem
              key={opt.value}
              value={itemValue}
              {...(opt.disabled !== undefined ? { disabled: opt.disabled } : {})}
            >
              {opt.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </RadixSelect.Root>
  );
});

// ---------------------------------------------------------------------------
// Dotted composition — matches Card/D-05 pattern
// ---------------------------------------------------------------------------

type SelectComposition = typeof ComposedSelect & {
  Root: typeof SelectRoot;
  Trigger: typeof SelectTrigger;
  Value: typeof SelectValue;
  Content: typeof SelectContent;
  Item: typeof SelectItem;
  Group: typeof SelectGroup;
  Label: typeof SelectLabel;
  Separator: typeof SelectSeparator;
  Portal: typeof SelectPortal;
};

export const Select = ComposedSelect as SelectComposition;
Select.Root = SelectRoot;
Select.Trigger = SelectTrigger;
Select.Value = SelectValue;
Select.Content = SelectContent;
Select.Item = SelectItem;
Select.Group = SelectGroup;
Select.Label = SelectLabel;
Select.Separator = SelectSeparator;
Select.Portal = SelectPortal;
